import React, { useEffect, useState } from 'react';
import { Container, Row, Col, Card, Badge, Spinner, Button, Modal } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import {
  fetchLunchPlanWaivers,
  selectWaivers,
  selectMeta,
  selectLoading,
  selectError
} from '../../store/lunchPlanWaiver/lunchPlanWaiverSlice';
import { getEffectiveStudentId } from '../../utilities/teacherModulePermissions';
import { formatDisplayDate } from '../../utilities/displayFormat';


const LunchPlanWaiverListComp = ({ data, viewMode }) => {
  // Always call hooks first
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const waivers = useSelector(selectWaivers);
  const meta = useSelector(selectMeta);
  const loading = useSelector(selectLoading);
  const error = useSelector(selectError);
  let profileData = useSelector((state) => state.setting.setting.user_data);
  profileData = profileData ? JSON.parse(profileData) : null;
  const [currentPage, setCurrentPage] = useState(1);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedWaiver, setSelectedWaiver] = useState(null);

  // Defined here (before the viewMode === 'profile' early return below)
  // rather than further down, since that branch renders and returns
  // before ever reaching a later definition - referencing formatDate from
  // inside it before this point would throw a ReferenceError.
  const formatDate = (dateString) => {
    if (!dateString || dateString === '1899-11-29' || dateString === 'Invalid DateTime') return 'N/A';
    return formatDisplayDate(dateString);
  };

  useEffect(() => {
    dispatch(fetchLunchPlanWaivers({ page: currentPage, limit: 10, student_id: getEffectiveStudentId(profileData) }));
  }, [dispatch, currentPage]);

  // In profile view, always use the passed-in data prop (already filtered by parent)
  if (viewMode === 'profile') {
    if (!Array.isArray(data) || data.length === 0) {
      // Show nothing if no active data (parent will show form)
      return null;
    }
    // Render only the passed-in data
    return (
      <Container>
        <Row>
          {data.map((waiver) => (
            <Col lg="12" key={waiver.id} className="mb-3">
              <Card className="shadow-sm">
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-start mb-3 card-row-head">
                    <div>
                      <h5 className="mb-2">
                        Lunch Plan Waiver #{waiver.id}
                        <Badge bg="success" className="ms-2">Active</Badge>
                      </h5>
                      <p className="text-muted mb-0">
                        <small>Submitted: {formatDate(waiver.fillup_date)}</small>
                      </p>
                    </div>
                    <div>
                      <Button
                        variant="info"
                        size="sm"
                        className="me-2 d-inline-flex align-items-center justify-content-center"
                        onClick={() => setShowDetailModal(true)}
                      >
                        <i className="icon material-symbols-outlined me-1">visibility</i>
                        View
                      </Button>
                      <Button
                        variant="primary"
                        size="sm"
                        className="d-inline-flex align-items-center justify-content-center"
                        onClick={() => navigate(`/dashboard/lunch-plan-waiver/edit/${waiver.id}`)}
                      >
                        <i className="icon material-symbols-outlined me-1">edit</i>
                        Edit
                      </Button>
                    </div>
                  </div>
                  <Row>
                    <Col md="6">
                      <p className="mb-2">
                        <strong>Reason:</strong>
                        <br />
                        {waiver.reason || 'N/A'}
                      </p>
                    </Col>
                    <Col md="6">
                      <div className="mb-3">
                        <p className="mb-2"><strong>Parent Signature:</strong></p>
                        {waiver.parents_signature ? (
                          <div className="border rounded p-2 bg-light mb-1">
                            <img 
                              src={waiver.parents_signature} 
                              alt="Parent Signature" 
                              style={{ maxWidth: '200px', maxHeight: '80px', display: 'block' }}
                            />
                          </div>
                        ) : (
                          <span className="text-muted">N/A</span>
                        )}
                        <small className="text-muted">Date: {formatDate(waiver.parent_signature_date)}</small>
                      </div>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
      </Container>
    );
  }
  
    const handleViewDetails = (waiver) => {
      // The list row already carries every field the modal renders (reason,
      // dates, both signatures) - TEACHERS_LUNCH_PLAN_WAIVER_API looks up
      // "the" active waiver for a student_id, not a specific waiver by id,
      // so calling it here re-fetched the same (active) record regardless
      // of which row was clicked, showing the wrong waiver's details for
      // every inactive/past entry.
      setSelectedWaiver(waiver);
      setShowDetailModal(true);
    };
  
    const handleCloseModal = () => {
      setShowDetailModal(false);
      setSelectedWaiver(null);
    };

    return (
      <Container fluid className="px-0">
        {/* Scoped styles matching the No Peanut Policy / Allergy Action
            Plan list screens. Cards are now full-width (one per row, see
            the Col below) rather than a multi-column grid, so there's room
            to show the reason and signatures without clamping/shrinking. */}
        <style>{`
          .lunch-waiver-card {
            border: 1px solid #eef0f3;
            border-radius: 12px;
            transition: transform 0.15s ease, box-shadow 0.15s ease;
          }
          .lunch-waiver-card:hover {
            transform: translateY(-2px);
            box-shadow: 0 10px 24px rgba(17, 24, 39, 0.08) !important;
          }
          .lunch-waiver-signature-box {
            height: 76px;
            display: flex;
            align-items: center;
            justify-content: center;
            background: #f8f9fa;
            border: 1px dashed #dcdfe3;
            border-radius: 8px;
            overflow: hidden;
          }
        `}</style>
        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" role="status" style={{ color: '#6f42c1' }}>
              <span className="visually-hidden">Loading...</span>
            </Spinner>
            <p className="mt-3 text-muted mb-0">Loading waivers...</p>
          </div>
        ) : error ? (
          <Card className="border-0 shadow-sm" style={{ borderRadius: '12px' }}>
            <Card.Body className="text-center py-5">
              <i className="icon material-symbols-outlined" style={{ fontSize: '48px', color: '#dc3545' }}>error</i>
              <p className="text-danger mt-2 mb-0">{error}</p>
            </Card.Body>
          </Card>
        ) : waivers.length === 0 ? (
          <Card className="border-0 shadow-sm" style={{ borderRadius: '12px' }}>
            <Card.Body className="text-center py-5">
              <i className="icon material-symbols-outlined" style={{ fontSize: '64px', color: '#ccc' }}>restaurant</i>
              <p className="mt-3 text-muted">No lunch plan waivers available</p>
              <Link to="/dashboard/lunch-plan-waiver/add">
                <Button variant="primary">Create Your First Waiver</Button>
              </Link>
            </Card.Body>
          </Card>
        ) : (
          <>
            <Row className="g-3">
              {waivers.map((waiver) => (
                <Col xs="12" key={waiver.id}>
                  <Card className="lunch-waiver-card shadow-sm">
                    <Card.Body>
                      <div className="d-flex justify-content-between align-items-start mb-3">
                        <div className="d-flex align-items-center gap-3">
                          <h6 className="mb-0 fw-bold text-dark">Lunch Plan Waiver #{waiver.id}</h6>
                          <Badge
                            bg={waiver.status === 1 ? "success" : "secondary"}
                            className="rounded-pill"
                            style={{ fontSize: '11px', fontWeight: 500, padding: '3px 10px', lineHeight: 1.3 }}
                          >
                            {waiver.status === 1 ? "Active" : "Inactive"}
                          </Badge>
                        </div>
                        <div className="d-flex align-items-center gap-3 flex-shrink-0">
                          <div className="d-flex gap-2">
                            <Button
                              variant="outline-info"
                              size="sm"
                              className="d-inline-flex align-items-center justify-content-center"
                              onClick={() => handleViewDetails(waiver)}
                            >
                              <i className="icon material-symbols-outlined me-1" style={{ fontSize: '16px' }}>visibility</i>
                              View
                            </Button>
                            {waiver.status === 1 && (
                              <Button
                                variant="primary"
                                size="sm"
                                className="d-inline-flex align-items-center justify-content-center"
                                onClick={() => navigate(`/dashboard/lunch-plan-waiver/edit/${waiver.id}`)}
                              >
                                <i className="icon material-symbols-outlined me-1" style={{ fontSize: '16px' }}>edit</i>
                                Edit
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                      <Row className="g-4 align-items-start">
                        <Col md="2">
                          <div className="text-muted mb-1" style={{ fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                            Submitted
                          </div>
                          <div style={{ fontSize: '14px' }}>
                            <i className="icon material-symbols-outlined me-1" style={{ fontSize: '14px', verticalAlign: 'middle' }}>calendar_today</i>
                            {formatDate(waiver.fillup_date)}
                          </div>
                        </Col>
                        <Col md="4">
                          <div className="text-muted mb-1" style={{ fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                            Reason
                          </div>
                          <div style={{ fontSize: '14px' }}>
                            {waiver.reason || <span className="text-muted fst-italic">Not provided</span>}
                          </div>
                        </Col>
                        <Col md="3">
                          <div className="text-muted mb-1" style={{ fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                            Parent Signature
                          </div>
                          <div className="lunch-waiver-signature-box">
                            {waiver.parents_signature ? (
                              <img
                                src={waiver.parents_signature}
                                alt="Parent Signature"
                                style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                              />
                            ) : (
                              <span className="text-muted" style={{ fontSize: '11px' }}>No signature</span>
                            )}
                          </div>
                        </Col>
                        <Col md="3">
                          <div className="text-muted mb-1" style={{ fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                            Supervisor Signature
                          </div>
                          <div className="lunch-waiver-signature-box">
                            {waiver.supervisor_signature ? (
                              <img
                                src={waiver.supervisor_signature}
                                alt="Supervisor Signature"
                                style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                              />
                            ) : (
                              <span className="text-muted" style={{ fontSize: '11px' }}>No signature</span>
                            )}
                          </div>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>

            {/* Pagination */}
            {meta && meta.last_page > 1 && (
              <div className="d-flex justify-content-center mt-4">
                <nav>
                  <ul className="pagination">
                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                      <button
                        className="page-link"
                        onClick={() => setCurrentPage(currentPage - 1)}
                        disabled={currentPage === 1}
                      >
                        Previous
                      </button>
                    </li>
                    {[...Array(meta.last_page)].map((_, index) => (
                      <li
                        key={index + 1}
                        className={`page-item ${currentPage === index + 1 ? 'active' : ''}`}
                      >
                        <button
                          className="page-link"
                          onClick={() => setCurrentPage(index + 1)}
                        >
                          {index + 1}
                        </button>
                      </li>
                    ))}
                    <li className={`page-item ${currentPage === meta.last_page ? 'disabled' : ''}`}>
                      <button
                        className="page-link"
                        onClick={() => setCurrentPage(currentPage + 1)}
                        disabled={currentPage === meta.last_page}
                      >
                        Next
                      </button>
                    </li>
                  </ul>
                </nav>
              </div>
            )}
          </>
        )}

        {/* Detail Modal - size="xl" (was "lg") so the full-width sections
            below actually use the available space. */}
        <Modal
          show={showDetailModal}
          onHide={handleCloseModal}
          centered
          size="xl"
        >
          <Modal.Header closeButton>
            <Modal.Title className="d-flex align-items-center gap-2">
              Lunch Plan Waiver #{selectedWaiver?.id} Details
              {selectedWaiver && (
                <Badge bg={selectedWaiver.status === 1 ? "success" : "secondary"} className="rounded-pill">
                  {selectedWaiver.status === 1 ? "Active" : "Inactive"}
                </Badge>
              )}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ maxHeight: '75vh', overflowY: 'auto' }}>
            {selectedWaiver && (
              <div>
                {/* Waiver Information */}
                <div className="mb-4 p-3" style={{ background: '#f8f9fa', border: '1px solid #eef0f3', borderRadius: '10px' }}>
                  <div className="d-flex align-items-center mb-3">
                    <div className="text-white rounded-circle d-flex align-items-center justify-content-center me-2" style={{ width: '32px', height: '32px', backgroundColor: '#6f42c1' }}>
                      <i className="icon material-symbols-outlined" style={{ fontSize: '18px' }}>restaurant</i>
                    </div>
                    <h6 className="mb-0 fw-bold">Waiver Information</h6>
                  </div>
                  <Row className="g-3">
                    <Col md="4">
                      <div className="text-muted" style={{ fontSize: '12px' }}>Submitted Date</div>
                      <div style={{ fontSize: '14px' }}>{formatDate(selectedWaiver.fillup_date)}</div>
                    </Col>
                    <Col md="8">
                      <div className="text-muted" style={{ fontSize: '12px' }}>Reason</div>
                      <div style={{ fontSize: '14px' }}>{selectedWaiver.reason || 'N/A'}</div>
                    </Col>
                  </Row>
                </div>

                {/* Signatures */}
                <div>
                  <div className="d-flex align-items-center mb-3">
                    <div className="text-white rounded-circle d-flex align-items-center justify-content-center me-2" style={{ width: '32px', height: '32px', backgroundColor: '#6f42c1' }}>
                      <i className="icon material-symbols-outlined" style={{ fontSize: '18px' }}>draw</i>
                    </div>
                    <h6 className="mb-0 fw-bold">Signatures</h6>
                  </div>
                  <Row className="g-3">
                    <Col md="6">
                      <div className="text-muted mb-1" style={{ fontSize: '12px' }}>Parent Signature</div>
                      {selectedWaiver.parents_signature ? (
                        <div className="border rounded p-2 bg-light mb-2">
                          <img
                            src={selectedWaiver.parents_signature}
                            alt="Parent Signature"
                            style={{ maxWidth: '100%', maxHeight: '120px', display: 'block' }}
                          />
                        </div>
                      ) : (
                        <p className="text-muted mb-2">N/A</p>
                      )}
                      <small className="text-muted">Date: {formatDate(selectedWaiver.parent_signature_date)}</small>
                    </Col>
                    <Col md="6">
                      <div className="text-muted mb-1" style={{ fontSize: '12px' }}>Supervisor Signature</div>
                      {selectedWaiver.supervisor_signature ? (
                        <div className="border rounded p-2 bg-light mb-2">
                          <img
                            src={selectedWaiver.supervisor_signature}
                            alt="Supervisor Signature"
                            style={{ maxWidth: '100%', maxHeight: '120px', display: 'block' }}
                          />
                        </div>
                      ) : (
                        <p className="text-muted">N/A</p>
                      )}
                    </Col>
                  </Row>
                </div>
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseModal}>
              Close
            </Button>
            {selectedWaiver && selectedWaiver.status === 1 && (
              <Button
                variant="primary"
                onClick={() => {
                  handleCloseModal();
                  navigate(`/dashboard/lunch-plan-waiver/edit/${selectedWaiver.id}`);
                }}
              >
                <i className="material-symbols-outlined me-1" style={{ verticalAlign: 'middle', fontSize: '18px' }}>edit</i>
                Edit Waiver
              </Button>
            )}
          </Modal.Footer>
        </Modal>
      </Container>
    );
};
export default LunchPlanWaiverListComp;
